import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getPropertyVendorsMap } from 'entities/Vendors';
import { SupplierBadge, SupplierBadgeItem } from 'shared/ui/SupplierBadge';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import { SuppliersStatusList } from './SuppliersStatusList';

import { sortSupplierProposals } from '../lib/sort/sortSupplierProposals';

interface SupplierBadgeWithTooltipProps {
    fullProposals: Proposal[];
    showCarriedByLabel?: boolean;
    dimmed?: boolean;
}

export const SupplierBadgeWithTooltip = ({
    fullProposals,
    showCarriedByLabel = false,
    dimmed,
}: SupplierBadgeWithTooltipProps) => {
    const { t } = useTranslation();
    const vendorsMap = useSelector(getPropertyVendorsMap);

    const sortedFullProposals = useMemo(
        () => sortSupplierProposals(fullProposals ?? [], vendorsMap),
        [fullProposals, vendorsMap],
    );

    const sortedBadgeProposals = useMemo<SupplierBadgeItem[]>(
        () =>
            sortedFullProposals.map((proposal) => ({
                uid: proposal.uid,
                sellerUid: proposal.seller.uid,
                name: proposal.seller.name,
            })),
        [sortedFullProposals],
    );

    if (sortedBadgeProposals.length === 0) return null;

    const badge = (
        <DynamicTooltip
            trigger="hover"
            interactive
            placement="top-start"
            containerStyle={{ display: 'inline-flex' }}
            content={
                <Box sx={{ minWidth: 280 }}>
                    <SuppliersStatusList proposals={sortedFullProposals} />
                </Box>
            }
        >
            <Box
                sx={{
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                }}
            >
                <SupplierBadge
                    proposals={sortedBadgeProposals}
                    opacity={dimmed ? 0.4 : 1}
                />
            </Box>
        </DynamicTooltip>
    );

    if (!showCarriedByLabel) {
        return badge;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '4px',
            }}
        >
            <Typography
                sx={{
                    fontFamily: 'Open Sans',
                    fontWeight: 400,
                    fontSize: '11px',
                    lineHeight: 'normal',
                    color: 'rgba(0, 0, 0, 0.5)',
                }}
            >
                {t('Carried by')}
            </Typography>
            {badge}
        </Box>
    );
};
